import type {CropRegion, Dimensions} from '../types';

export type FittedDimensions = Dimensions & {
  /** `false` when the image already fits; the pipeline then skips the resize entirely. */
  readonly scaled: boolean;
};

/** Shrinks so the long edge is at most `maxLongEdge`; never enlarges; keeps the aspect ratio. */
export const fitWithin = (dimensions: Dimensions, maxLongEdge: number): FittedDimensions => {
  const longEdge = Math.max(dimensions.width, dimensions.height);
  if (longEdge <= maxLongEdge) {
    return {width: dimensions.width, height: dimensions.height, scaled: false};
  }
  const scale = maxLongEdge / longEdge;
  return {
    width: Math.max(1, Math.round(dimensions.width * scale)),
    height: Math.max(1, Math.round(dimensions.height * scale)),
    scaled: true,
  };
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/** Intersects a crop with the image bounds on integer pixels; `null` when nothing remains. */
export const clampCropRegion = (crop: CropRegion, bounds: Dimensions): CropRegion | null => {
  const left = clamp(Math.floor(crop.x), 0, bounds.width);
  const top = clamp(Math.floor(crop.y), 0, bounds.height);
  const right = clamp(Math.ceil(crop.x + crop.width), 0, bounds.width);
  const bottom = clamp(Math.ceil(crop.y + crop.height), 0, bounds.height);
  if (right <= left || bottom <= top) {
    return null;
  }
  return {x: left, y: top, width: right - left, height: bottom - top};
};
